/** @format */

import React, { memo, type MouseEventHandler, useMemo, useState } from "react";
import { type VariantProps, tv } from "tailwind-variants";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import { adaptColorBrightness } from "../../../styles/helpers";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";

export const TagVariant = tv({
  base: "relative inline-flex h-6 flex-row items-center justify-center rounded-md px-1.5 py-0 focus-visible:outline-none focus-visible:ring-1 ring-offset-2 body-sm-bold text-center text-current",
  variants: {
    color: {
      red: "bg-tag-surface-red text-tag-text-red ring-tag-surface-red",
      rose: "bg-tag-surface-rose text-tag-text-rose ring-tag-surface-rose",
      purple:
        "bg-tag-surface-purple text-tag-text-purple ring-tag-surface-purple",
      blue: "bg-tag-surface-blue text-tag-text-blue ring-tag-surface-blue",
      teal: "bg-tag-surface-teal text-tag-text-teal ring-tag-surface-teal",
      green: "bg-tag-surface-green text-tag-text-green ring-tag-surface-green",
      yellow:
        "bg-tag-surface-yellow text-tag-text-yellow ring-tag-surface-yellow",
      "light-grey":
        "bg-tag-surface-light-grey text-tag-text-light-grey ring-tag-surface-light-grey",
      grey: "bg-tag-surface-grey text-tag-text-grey ring-tag-surface-grey",
      brown: "bg-tag-surface-brown text-tag-text-brown ring-tag-surface-brown",
      orange:
        "bg-tag-surface-orange text-tag-text-orange ring-tag-surface-orange",
    },
    interactive: {
      true: "cursor-pointer",
      false: "cursor-initial",
    },
    selected: {
      true: "",
    },
  },
  compoundVariants: [
    {
      color: "red",
      interactive: true,
      class: "hover:bg-tag-surface-hover-red",
    },
    {
      color: "rose",
      interactive: true,
      class: "hover:bg-tag-surface-hover-rose",
    },
    {
      color: "purple",
      interactive: true,
      class: "hover:bg-tag-surface-hover-purple",
    },
    {
      color: "blue",
      interactive: true,
      class: "hover:bg-tag-surface-hover-blue",
    },
    {
      color: "teal",
      interactive: true,
      class: "hover:bg-tag-surface-hover-teal",
    },
    {
      color: "green",
      interactive: true,
      class: "hover:bg-tag-surface-hover-green",
    },
    {
      color: "yellow",
      interactive: true,
      class: "hover:bg-tag-surface-hover-yellow",
    },
    {
      color: "orange",
      interactive: true,
      class: "hover:bg-tag-surface-hover-orange",
    },
    {
      color: "brown",
      interactive: true,
      class: "hover:bg-tag-surface-hover-brown",
    },
    {
      color: "grey",
      interactive: true,
      class: "hover:bg-tag-surface-hover-grey",
    },
    {
      color: "light-grey",
      interactive: true,
      class: "hover:bg-tag-surface-hover-light-grey",
    },
    {
      color: "red",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-red hover:bg-tag-surface-selected-red",
    },
    {
      color: "rose",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-rose hover:bg-tag-surface-selected-rose",
    },
    {
      color: "purple",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-purple hover:bg-tag-surface-selected-purple",
    },
    {
      color: "blue",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-blue hover:bg-tag-surface-selected-blue",
    },
    {
      color: "teal",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-teal hover:bg-tag-surface-selected-teal",
    },
    {
      color: "green",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-green hover:bg-tag-surface-selected-green",
    },
    {
      color: "yellow",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-yellow hover:bg-tag-surface-selected-yellow",
    },
    {
      color: "orange",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-orange hover:bg-tag-surface-selected-orange",
    },
    {
      color: "brown",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-brown hover:bg-tag-surface-selected-brown",
    },
    {
      color: "grey",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-grey hover:bg-tag-surface-selected-grey",
    },
    {
      color: "light-grey",
      selected: true,
      class:
        "text-surface-default bg-tag-surface-selected-light-grey hover:bg-tag-surface-selected-light-grey",
    },
  ],
});

const TagClose = tv({
  base: "group/close relative duration-300 ease-expressive-out rounded-full text-current",
  variants: {
    clicked: {
      true: "opacity-100 ml-1 -mr-0.5",
      false: "pointer-events-none opacity-0 mr-[calc(2px-1.25em)] scale-50",
    },
    color: {
      red: "hover:text-tag-surface-red hover:bg-tag-text-red",
      rose: "hover:text-tag-surface-rose hover:bg-tag-text-rose",
      purple: "hover:text-tag-surface-purple hover:bg-tag-text-purple",
      blue: "hover:text-tag-surface-blue hover:bg-tag-text-blue",
      teal: "hover:text-tag-surface-teal hover:bg-tag-text-teal",
      green: "hover:text-tag-surface-green hover:bg-tag-text-green",
      yellow: "hover:text-tag-surface-yellow hover:bg-tag-text-yellow",
      "light-grey":
        "grey hover:text-tag-surface-light-grey hover:bg-tag-text-light-grey",
      grey: "hover:text-tag-surface-grey hover:bg-tag-text-grey",
      brown: "hover:text-tag-surface-brown hover:bg-tag-text-brown",
      orange: "hover:text-tag-surface-orange hover:bg-tag-text-orange",
    },
  },
});

export interface TagProps
  extends Omit<VariantProps<typeof TagVariant>, "color"> {
  color?: VariantProps<typeof TagVariant>["color"] | string;
  text?: JSX.Element | string;
  selected?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  removeIcon?: boolean;
  handleRemoveClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
}

const Tag = ({
  text,
  color,
  selected,
  removeIcon,
  onClick,
  handleRemoveClick,
  className,
  interactive = true,
  ...props
}: TagProps) => {
  const [clicked, setClicked] = useState(false);

  const container = React.useRef<HTMLDivElement | null>(null);
  useOnClickOutside(container, () => setClicked(false));

  const style = useMemo(
    () => ({
      "--bg-selected": adaptColorBrightness(color, -20),
      "--bg-hover": adaptColorBrightness(color, 10),
      "--bg-default": adaptColorBrightness(color, 17),
      // '--text-selected': "#FFF",
      "--text-default": adaptColorBrightness(color, -50),
    }),
    [color],
  );

  const isCustomColor = useMemo(() => {
    return !Object.keys(TagVariant.variants.color).includes(color ?? "");
  }, [color]);

  const containerVariant = TagVariant({
    color,
    interactive,
    selected,
    className: cn(
      isCustomColor && "bg-[var(--bg-default)] text-[var(--text-default)]",
      isCustomColor && interactive && "hover:bg-[var(--bg-hover)]",
      isCustomColor &&
        selected &&
        "bg-[var(--bg-selected)] text-surface-default",
    ),
  });
  const closeVariant = TagClose({
    color: (color ?? "grey") as VariantProps<typeof TagClose>["color"],
    clicked: clicked || removeIcon,
    className: cn(
      isCustomColor &&
        interactive &&
        "hover:bg-[var(--text-default)] hover:text-[var(--bg-default)]",
      isCustomColor &&
        selected &&
        "bg-[var(--bg-selected)] text-surface-default",
    ),
  });

  return (
    <div
      ref={container}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={cn(containerVariant, className)}
      style={style as unknown as React.CSSProperties}
      onClick={(e) => {
        if (!interactive) return;
        onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
        if (text !== "+") {
          setClicked((prev) => !prev);
        }
      }}
      {...props}
    >
      <span>{text}</span>
      {removeIcon && (
        <button
          type="button"
          className={closeVariant}
          onMouseDown={handleRemoveClick}
          tabIndex={0}
        >
          <Icon
            name="x-md"
            className="text-current -m-0.5 w-[1.25em] h-[1.25em]"
          />
        </button>
      )}
    </div>
  );
};

export default memo(Tag);
